/* LP202610: independent of Impulse, jQuery and the store's app scripts. */
(() => {
  'use strict';

  const selector = '[data-lp202610]';
  const instances = new WeakMap();
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  function mount(root) {
    if (instances.has(root)) return;
    const story = root.querySelector('[data-lp202610-story]');
    const panel = root.querySelector('[data-lp202610-panel]');
    const media = root.querySelector('.lardini-lp202610__hero-media');
    const lead = root.querySelector('[data-lp202610-lead]');
    const title = root.querySelector('[data-lp202610-title]');
    if (!story || !panel || !media || !lead || !title) return;

    const abort = new AbortController();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia('(max-width: 780px)');
    const reveals = [...root.querySelectorAll('[data-lp202610-reveal]')];
    const photos = [...root.querySelectorAll('[data-lp202610-photo]')];
    let frame = 0;
    let observer;
    let photoObserver;
    let headerOffset = 0;
    let mobileWidth;
    let previousLeadVisibility;

    function measureHeader() {
      const header = document.querySelector('[data-section-id="header"]');
      headerOffset = 0;
      if (mobile.matches && header && getComputedStyle(header).position === 'fixed') {
        headerOffset = Math.max(0, header.getBoundingClientRect().bottom);
      }
      root.style.setProperty('--lp202610-fixed-header', `${headerOffset}px`);
    }

    function measureLayout() {
      measureHeader();
      if (!mobile.matches) {
        mobileWidth = undefined;
        root.style.removeProperty('--lp202610-screen-height');
        return;
      }

      // Browser chrome, the keyboard and announcement bars can resize the
      // viewport/header while scrolling. Only a width change resizes the image
      // and its three-panel scroll track. The sticky top still follows the header.
      const width = document.documentElement.clientWidth;
      if (mobileWidth === width) return;
      root.style.removeProperty('--lp202610-screen-height');
      const height = media.getBoundingClientRect().height;
      if (height > 0) {
        root.style.setProperty('--lp202610-screen-height', `${height}px`);
        mobileWidth = width;
      }
    }

    function update() {
      frame = 0;
      if (reducedMotion.matches) return;
      const height = panel.getBoundingClientRect().height;
      if (!height) return;
      const progress = clamp((headerOffset - story.getBoundingClientRect().top) / height, 0, 2);
      const titleOpacity = 1 - clamp(progress / .4);
      const darkness = progress <= 1 ? .3 * progress : .3 + .4 * (progress - 1);
      const leadOpacity = clamp((progress - 1.3) / .6);
      root.style.setProperty('--lp202610-title-opacity', titleOpacity.toFixed(4));
      root.style.setProperty('--lp202610-hero-darkness', darkness.toFixed(4));
      root.style.setProperty('--lp202610-lead-opacity', leadOpacity.toFixed(4));

      const leadVisible = leadOpacity > .05;
      if (previousLeadVisibility !== leadVisible) {
        lead.setAttribute('aria-hidden', String(!leadVisible));
        previousLeadVisibility = leadVisible;
      }
      // Keep the page's h1 accessible while its visual title transitions out.
    }

    function schedule() {
      if (!frame) frame = requestAnimationFrame(update);
    }

    function configure() {
      observer?.disconnect();
      photoObserver?.disconnect();
      measureLayout();
      previousLeadVisibility = undefined;
      if (reducedMotion.matches) {
        root.classList.remove('is-enhanced');
        lead.removeAttribute('aria-hidden');
        root.style.setProperty('--lp202610-hero-darkness', '0');
        root.style.setProperty('--lp202610-title-opacity', '1');
        root.style.setProperty('--lp202610-lead-opacity', '1');
        reveals.forEach(element => element.classList.add('is-visible'));
        photos.forEach(element => element.classList.add('is-visible'));
        return;
      }

      root.classList.add('is-enhanced');
      update();
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        }, { threshold: .15 });
        reveals.forEach(element => observer.observe(element));
        // A tall photograph can exceed the viewport; enter detection uses its edge.
        photoObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const image = entry.target;
              photoObserver.unobserve(image);
              // Reveal a lazy image once its pixels are decoded, not an empty slot.
              image.decode().catch(() => {}).then(() => {
                if (!abort.signal.aborted) image.classList.add('is-visible');
              });
            }
          });
        }, { threshold: 0 });
        photos.forEach(element => photoObserver.observe(element));
      } else {
        reveals.forEach(element => element.classList.add('is-visible'));
        photos.forEach(element => element.classList.add('is-visible'));
      }
    }

    function resize() {
      measureLayout();
      schedule();
    }

    window.addEventListener('scroll', schedule, { passive: true, signal: abort.signal });
    window.addEventListener('resize', resize, { passive: true, signal: abort.signal });
    window.addEventListener('pageshow', resize, { signal: abort.signal });
    reducedMotion.addEventListener('change', configure, { signal: abort.signal });
    mobile.addEventListener('change', resize, { signal: abort.signal });

    const header = document.querySelector('[data-section-id="header"]');
    const headerObserver = header && 'ResizeObserver' in window ? new ResizeObserver(resize) : null;
    headerObserver?.observe(header);
    configure();

    instances.set(root, () => {
      abort.abort();
      observer?.disconnect();
      photoObserver?.disconnect();
      headerObserver?.disconnect();
      cancelAnimationFrame(frame);
      root.classList.remove('is-enhanced');
      root.style.removeProperty('--lp202610-screen-height');
      lead.removeAttribute('aria-hidden');
      instances.delete(root);
    });
  }

  function mountWithin(scope) {
    if (scope.matches?.(selector)) mount(scope);
    scope.querySelectorAll(selector).forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mountWithin(document), { once: true });
  } else {
    mountWithin(document);
  }

  // In the theme editor, section rendering must not duplicate scroll handlers.
  document.addEventListener('shopify:section:load', event => mountWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    const roots = event.target.matches?.(selector)
      ? [event.target]
      : event.target.querySelectorAll(selector);
    roots.forEach(root => instances.get(root)?.());
  });
})();
